import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChartBar, ArrowsClockwise, CheckCircle, WarningCircle, Star, Path, Pulse } from "@phosphor-icons/react";

const PRIO_STYLE = {
  CRITICAL: "bg-red-500/15 text-red-300 border-red-500/40",
  HIGH: "bg-accent/15 text-accent border-accent/40",
  NORMAL: "bg-surface-2 text-muted border-border",
  DISABLED: "bg-zinc-700/30 text-zinc-500 border-zinc-600/40 line-through",
};
const HEALTH = {
  ok: { label: "À jour", cls: "text-emerald-400", dot: "bg-emerald-400" },
  warn: { label: "> 24 h", cls: "text-amber-400", dot: "bg-amber-400" },
  stale: { label: "Aucun signal récent", cls: "text-red-400", dot: "bg-red-400" },
  none: { label: "Jamais reçu", cls: "text-zinc-500", dot: "bg-zinc-500" },
};

function fmtAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `il y a ${mins} min`;
  const h = Math.round(mins / 60);
  if (h < 48) return `il y a ${h} h`;
  return `il y a ${Math.round(h / 24)} j`;
}

export default function SourceCoveragePanel() {
  const [items, setItems] = useState([]);
  const [levels, setLevels] = useState(["DISABLED", "NORMAL", "HIGH", "CRITICAL"]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cov, prio] = await Promise.all([
        api.get("/admin/sources/coverage"),
        api.get("/admin/sources/priorities"),
      ]);
      setItems(cov.data.items || []);
      setLevels(prio.data.levels || levels);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const setPriority = async (row, priority) => {
    setSaving(row.source_id);
    try {
      await api.put(`/admin/sources/priorities/${row.source_id}`, { priority, france_it: row.france_it });
      setItems((prev) => prev.map((x) => x.source_id === row.source_id ? { ...x, priority } : x));
      toast.success(`${row.label} → priorité ${priority}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(null); }
  };

  return (
    <div className="surface rounded-xl p-5 mb-6 border border-border" data-testid="source-coverage-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <ChartBar size={20} weight="duotone" className="text-accent" />
          <h2 className="font-display text-lg font-bold">Couverture par source d'origine</h2>
        </div>
        <button onClick={load} data-testid="coverage-refresh" className="flex items-center gap-1.5 text-xs text-muted hover:text-fg border border-border rounded-md px-3 py-1.5">
          <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>
      <p className="text-xs text-muted mb-4 max-w-3xl">Éditeur RÉEL derrière chaque annonce (HelloWork, APEC, Cadremploi, Talent.com…), quelle que soit la route d'ingestion (France Travail, Google Jobs, Bright Data…). KPI clé : <strong className="text-fg">signaux uniques utiles</strong> par éditeur, fraîcheur & doublons. Réglez la priorité de sourcing par source.</p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border text-muted">
              {["Source d'origine", "Priorité", "Total", "IT", "24 h", "7 j", "Doublons", "Délai médian", "Dernier signal", "Santé", "Routes d'ingestion"].map((h, i) => (
                <th key={i} className="px-3 py-2 overline font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-muted">Chargement…</td></tr>
            )}
            {items.map((it) => {
              const hl = HEALTH[it.health] || HEALTH.none;
              return (
                <tr key={it.source_id} className={`border-b border-border/50 hover:bg-surface-2/40 ${it.france_it ? "bg-accent/[0.03]" : ""}`} data-testid={`coverage-row-${it.source_id}`}>
                  <td className="px-3 py-2.5 font-medium text-fg whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      {it.france_it && <Star size={13} weight="fill" className="text-accent" title="Source stratégique France / IT" />}
                      {it.label}
                    </span>
                    {it.coverage_gap && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-400 border border-amber-500/40 rounded px-1.5 py-0.5" data-testid={`coverage-gap-${it.source_id}`} title="Priorité élevée mais très peu de signaux uniques récents — envisager une route supplémentaire.">
                        <WarningCircle size={10} weight="fill" /> COUVERTURE FAIBLE
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <Select value={it.priority} onValueChange={(v) => setPriority(it, v)} disabled={saving === it.source_id}>
                      <SelectTrigger className={`h-7 w-[116px] text-xs font-semibold border rounded-md ${PRIO_STYLE[it.priority] || PRIO_STYLE.NORMAL}`} data-testid={`priority-select-${it.source_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-fg">{it.total}</td>
                  <td className="px-3 py-2.5 font-mono text-muted">{it.it_count}</td>
                  <td className="px-3 py-2.5 font-mono text-muted">{it.jobs_24h}</td>
                  <td className="px-3 py-2.5 font-mono text-muted">{it.jobs_7d}</td>
                  <td className="px-3 py-2.5 font-mono text-muted">{it.duplicates}</td>
                  <td className="px-3 py-2.5 font-mono text-muted whitespace-nowrap">{it.median_detection_days == null ? "—" : `${it.median_detection_days} j`}</td>
                  <td className="px-3 py-2.5 text-muted whitespace-nowrap text-xs">{fmtAgo(it.last_signal)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 text-xs ${hl.cls}`}>
                      <span className={`w-2 h-2 rounded-full ${hl.dot}`} /> {hl.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(it.routes || []).length === 0 && <span className="text-xs text-muted">—</span>}
                      {(it.routes || []).map((r) => (
                        <span key={r.provider} className="inline-flex items-center gap-1 text-[10px] font-mono bg-surface-2 border border-border rounded px-1.5 py-0.5 text-muted" title={`${r.count} observation(s) via ${r.provider}`}>
                          <Path size={9} /> {r.provider} {r.pct}%
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-border text-[11px] text-muted">
        <span className="inline-flex items-center gap-1.5"><Star size={11} weight="fill" className="text-accent" /> Source stratégique France / IT (HIGH par défaut)</span>
        <span className="inline-flex items-center gap-1.5"><Pulse size={11} className="text-emerald-400" /> Santé = fraîcheur du dernier signal reçu</span>
        <span className="inline-flex items-center gap-1.5"><WarningCircle size={11} className="text-amber-400" /> COUVERTURE FAIBLE = priorité haute mais peu de signaux → route directe à envisager</span>
        <span>DISABLED = les observations de cette source sont ignorées à l'ingestion (sans casser l'agrégateur partagé)</span>
      </div>
    </div>
  );
}
